import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from 'sonner';
import { db } from "@/lib/supabaseQuery";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/utils/logger";
import { useLocalizations } from "@/hooks/useLocalizations";

export interface NotificationPreferences {
  id: string;
  user_id: string;
  opportunities_enabled: boolean;
  updates_enabled: boolean;
  updates_location_ids: string[];
  marketing_enabled: boolean;
  mass_mail_status: string | null;
  mailerlite_subscriber_id: string | null;
  mailerlite_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateNotificationPreferences {
  opportunities_enabled?: boolean;
  updates_enabled?: boolean;
  updates_location_ids?: string[];
  marketing_enabled?: boolean;
}

export function useNotificationPreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { lCommon } = useLocalizations();

  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ["notification-preferences", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const data = await db.fetchOne<NotificationPreferences>('user_notification_preferences', '*', [
        { column: 'user_id', operator: 'eq', value: user.id }
      ]);

      // If no preferences exist, create default ones
      if (!data) {
        const newPrefs = await db.insert<NotificationPreferences>('user_notification_preferences', { user_id: user.id }, { returning: true });
        return newPrefs as NotificationPreferences;
      }

      return data;
    },
    enabled: !!user?.id,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: UpdateNotificationPreferences) => {
      if (!user?.id) throw new Error("Not authenticated");

      const result = await db.update<NotificationPreferences>('user_notification_preferences', updates, [
        { column: 'user_id', operator: 'eq', value: user.id }
      ], { returning: true });

      if (!result || result.length === 0) throw new Error("Update failed");
      return result[0];
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["notification-preferences", user?.id], data);
      toast.success(lCommon('toast.preferences_saved.success', { defaultValue: 'Preferences saved' }));
      // NOTE: MailerLite sync now handled by database trigger or scheduled workflow
      // The sync-mailerlite-subscriber edge function is no longer invoked from browser
    },
    onError: (error) => {
      toast.error(lCommon('toast.preferences_save_failed.error', { defaultValue: 'Failed to save preferences' }));
      logger.error('Update notification preferences error', { error });
    },
  });

  return {
    preferences,
    isLoading,
    error,
    updatePreferences: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}